import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import Button from "../../components/Common/Button";
import IconToggle from "../Common/IconToggle";

import { IS_NBA_MODE } from "../../const/config";
import { FavoriteType } from "../../const/enums/FavoriteType";
import { PROFILE_METRICS_RANK } from "../../const/profile";
import { IProfile, ProfileCategory } from "../../interfaces/profile";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { toggleFavorite } from "../../redux/slices/reportSlice";
import { toUSDFormat } from "../../utils";

import StarIcon from "../icons/StarIcon";
import StarIconEmpty from "../icons/StarIconEmpty";

interface Props {
  profile: IProfile;
  category: string;
}

export default function MainProfile({ profile, category }: Props) {
  const dispatch = useAppDispatch();

  const { currentProfile } = useAppSelector((state) => state.profile);
  const { favorites } = useAppSelector((state) => state.report);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);

  const handleFollow = () => {
    let favoriteType = FavoriteType.Profile;
    let favoriteId = 1;
    let favoriteName = `${category} - ${profile.name}`;
    let favoriteImageLink = profile.photo ?? "";

    toast.promise(
      dispatch(
        toggleFavorite({
          favoriteType,
          favoriteId,
          favoriteName,
          favoriteImageLink,
        })
      ).unwrap(),
      {
        loading: "Following...",
        success: "Successfully followed the profile",
        error: "Oops! Something went wrong while trying to follow the profile.",
      }
    );
  };

  useEffect(() => {
    const checkFavorite = (type: FavoriteType, profileName: string) =>
      favorites.some(
        (favorite) =>
          favorite.favoriteType === type &&
          favorite.favoriteId === 1 &&
          favorite.favoriteName === `${category} - ${profile.name}`
      );
    setIsFollowing(checkFavorite(FavoriteType.Profile, profile.name));
  }, [category, favorites, profile.name]);

  return (
    <div>
      <div
        style={{
          background: "var(--bg-profile-gradient)",
        }}
      >
        <div className="flex gap-2">
          <ProfileImage
            src={
              profile.photo?.startsWith("http")
                ? profile.photo
                : category === ProfileCategory.Player
                ? "/assets/imgs/user_square.png"
                : "/assets/imgs/users_square.png"
            }
          />

          <div className="py-4">
            {profile.role && (
              <div className="uppercase text-custom-white-90 text-xs">
                {profile.role}
              </div>
            )}
            <div className="text-primaryText font-semibold text-lg py-1">
              {profile.name}
            </div>
            {/* <div className="text-custom-white-70 text-sm">Little Rock</div> */}
            <div className="mt-3">
              <Button
                bgColor="bg-secondary"
                className="flex !h-[28px] px-[10px] py-[7px] items-center gap-[10px] rounded-[8px] text-black"
                onClick={handleFollow}
              >
                <IconToggle
                  value={isFollowing}
                  onValueChange={() => {}}
                  iconOn={<StarIcon className="size-4" />}
                  iconOff={<StarIconEmpty className="size-4 " />}
                ></IconToggle>

                <span className="text-custom-buttonText font-primary text-xs not-italic font-semibold leading-[120%]">
                  {isFollowing ? "Unfollow" : "Follow"}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div className="p-3 flex justify-around text-sm border-b border-neutral-900">
          <div className="flex items-center gap-2">
            <PhoneIcon className="size-4" />
            <span>801-473-8272</span>
          </div>
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="size-4" />
            <span>Vivint@NBA.com</span>
          </div>
        </div>

        <div className="p-3 flex justify-around text-sm border-b border-neutral-900 text-center">
          {currentProfile?.entity_top_level && (
            <div>
              <div className="uppercase text-neutral-200">
                {currentProfile?.entity_top_level}
              </div>
              <div>{currentProfile?.entity_top_value}</div>
            </div>
          )}

          {currentProfile?.entity_top2_level && (
            <div>
              <div className="uppercase text-neutral-200">
                {currentProfile?.entity_top2_level}
              </div>
              <div>{currentProfile?.entity_top2_value}</div>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 flex w-full justify-between text-sm border-t border-b border-neutral-900 text-center">
        <div className="grid grid-cols-3 w-full">
          {(IS_NBA_MODE
            ? PROFILE_METRICS_RANK["NBA"]
            : PROFILE_METRICS_RANK["VSPN"]
          ).map((metricRank, index) => {
            const metric = (
              IS_NBA_MODE
                ? PROFILE_METRICS_RANK["NBA"]
                : PROFILE_METRICS_RANK["VSPN"]
            )[index];
            return (
              <div className="text-center py-3" key={index}>
                <div className="text-custom-white-80 text-xs">
                  {metric.toUpperCase()}
                </div>
                <div className="text-white mt-1 text-2xl font-bold">
                  {toUSDFormat(profile[metricRank] ?? 0)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ProfileImage = ({ src }: { src: string }) => (
  <svg
    width="140"
    height="140"
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <clipPath id="clipPolygon">
        <path d="M 0 100 L 0 0 L 100 0 C 92 26 89 46 77 68 C 66 83 56 88 43 93 C 28 97 10 100 0 100 Z" />
      </clipPath>

      <linearGradient id="gradientStroke" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F8F8F8" />
        <stop offset="100%" stopColor="#CEC39E" />
      </linearGradient>
    </defs>

    <image
      href={src}
      x="0"
      y="0"
      width="100"
      height="100"
      clipPath="url(#clipPolygon)"
      preserveAspectRatio="xMidYMid slice"
    />

    <path
      d="M 100 0 C 92 26 89 46 77 68 C 66 83 56 88 43 93 C 28 97 10 100 0 100"
      fill="none"
      stroke="url(#gradientStroke)"
      strokeWidth="2"
    />
  </svg>
);
